import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Box, Dialog, IconButton, Typography } from '@mui/material'
import { AppGreen } from 'appconstants/AppColors'
import { useRef, useState } from 'react'

type CheckInRequiredSheetProps = {
  openSheet: boolean
  message: string
  onDismiss: () => void
  onCheckIn: () => void
}

const THUMB_SIZE = 48
const SWIPE_THRESHOLD = 0.75

const SwipeToCheckInButton = ({ onComplete }: { onComplete: () => void }) => {
  const [offsetX, setOffsetX] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startXRef = useRef(0)
  const offsetXRef = useRef(0)
  const isCompletedRef = useRef(false)
  const trackRef = useRef<HTMLDivElement>(null)

  const maxOffset = trackRef.current ? trackRef.current.offsetWidth - THUMB_SIZE - 8 : 260

  const trackBg = '#E0F2E9'
  const trackBorder = `1px solid ${AppGreen}`
  const thumbBg = AppGreen
  const labelColor = AppGreen

  const handleStart = (clientX: number) => {
    if (isCompletedRef.current) return
    setIsDragging(true)
    startXRef.current = clientX - offsetX
  }

  const handleMove = (clientX: number) => {
    if (!isDragging || isCompletedRef.current) return
    const newOffset = Math.max(0, Math.min(clientX - startXRef.current, maxOffset))
    offsetXRef.current = newOffset
    setOffsetX(newOffset)
  }

  const handleEnd = () => {
    if (isCompletedRef.current) return
    setIsDragging(false)
    if (offsetXRef.current >= maxOffset * SWIPE_THRESHOLD) {
      isCompletedRef.current = true
      offsetXRef.current = maxOffset
      setOffsetX(maxOffset)
      onComplete()
      setTimeout(() => {
        offsetXRef.current = 0
        setOffsetX(0)
      }, 200)
    } else {
      offsetXRef.current = 0
      setOffsetX(0)
    }
  }

  return (
    <Box
      ref={trackRef}
      onMouseMove={(e) => handleMove(e.clientX)}
      onMouseUp={handleEnd}
      onMouseLeave={() => isDragging && handleEnd()}
      onTouchMove={(e) => handleMove(e.touches[0].clientX)}
      onTouchEnd={handleEnd}
      sx={{
        position: 'relative',
        width: '100%',
        height: THUMB_SIZE + 8,
        borderRadius: '30px',
        background: trackBg,
        border: trackBorder,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        userSelect: 'none',
        touchAction: 'none',
      }}
    >
      <Typography
        sx={{
          fontSize: 13,
          fontWeight: 600,
          color: labelColor,
          pointerEvents: 'none',
          opacity: offsetX > maxOffset * 0.3 ? 0 : 1,
          transition: 'opacity 0.2s',
          pl: `${THUMB_SIZE}px`,
        }}
      >
        Swipe to check-in
      </Typography>

      <Box
        onMouseDown={(e) => handleStart(e.clientX)}
        onTouchStart={(e) => handleStart(e.touches[0].clientX)}
        sx={{
          position: 'absolute',
          left: 4 + offsetX,
          top: 4,
          width: THUMB_SIZE,
          height: THUMB_SIZE,
          borderRadius: '50%',
          background: thumbBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: isDragging ? 'none' : 'left 0.3s ease',
          cursor: 'grab',
          '&:active': { cursor: 'grabbing' },
        }}
      >
        <CheckCircleRoundedIcon sx={{ color: '#fff', fontSize: 22 }} />
      </Box>
    </Box>
  )
}

const CheckInRequiredSheet = ({
  openSheet,
  message,
  onDismiss,
  onCheckIn,
}: CheckInRequiredSheetProps) => {
  return (
    <Dialog
      open={openSheet}
      onClose={onDismiss}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 48px)', sm: 420 },
          maxWidth: 420,
          borderRadius: '16px',
          m: 0,
        },
      }}
      sx={{ zIndex: 1300 }}
    >
      <Box sx={{ position: 'relative', px: 3, pt: 4, pb: 3 }}>
        <IconButton
          aria-label='Close check-in required popup'
          onClick={onDismiss}
          sx={{ position: 'absolute', top: 8, right: 8 }}
        >
          <CloseRoundedIcon sx={{ fontSize: 22 }} />
        </IconButton>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              bgcolor: '#F59E0B',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
            }}
          >
            <ErrorOutlineRoundedIcon sx={{ color: '#FFFFFF', fontSize: 40 }} />
          </Box>

          <Typography
            sx={{
              fontWeight: 600,
              fontSize: 16,
              lineHeight: '22px',
              mb: 1.5,
            }}
          >
            Check-in required
          </Typography>

          <Typography
            sx={{
              fontWeight: 500,
              fontSize: 16,
              lineHeight: '22px',
              textAlign: 'center',
              color: '#333333',
              mb: 3,
            }}
          >
            {message}
          </Typography>

          <SwipeToCheckInButton onComplete={onCheckIn} />
        </Box>
      </Box>
    </Dialog>
  )
}

export default CheckInRequiredSheet
